import searchIcon from '@jetbrains/icons/search.svg'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import Select from '@jetbrains/ring-ui/components/select/select'
import debounce from 'lodash/debounce'
import React, {memo, useCallback, useContext, useEffect, useMemo, useRef} from 'react'

import {restApi} from '../../../../../../../../../../../../services/rest'
import {emptyArray} from '../../../../../../../../../../../../utils/empty'
import {DisabledContext} from '../../../../../../../../../contexts/DisabledContext'
import useBooleanState from '../../../../../../../../../hooks/useBooleanState'
import {isDockerAutocompleteEnabled} from '../../../../../../../../../utils/featureToggles'
import CliErrorMessage from '../../SmartDetectors/CliErrorMessage/CliErrorMessage'
import {DOCKER_IMAGE_PROPERTY_NAME} from '../JobStepRunInDocker.consts'
import type {JobStepRunInDockerProps} from '../JobStepRunInDocker.types'
import {getDockerValues} from '../JobStepRunInDocker.utils'

import {
  getSelectImagesOptions,
  getSelectTagsOptions,
  parseDockerImage,
} from './JobStepRunInDockerImageSelect.utils'

import styles from './JobStepRunInDockerImageSelect.css'

const THROTTLE_TIME = 700

function ErrorMessage() {
  return (
    <div className={styles.errorMessage}>
      {"Something went wrong and we couldn't get the Docker image from hub.docker.com. "}
      {'Сlear the search  and try again'}
    </div>
  )
}

function NotFoundMessage({image}: {image: string}) {
  return (
    <div className={styles.notFoundMessage}>
      {'There are no results for '}
      <span className={styles.notFoundMessageCurrentValue}>{`"${image}" `}</span>
      {'in Docker Hub. '}
      {'Your build will probably fail if you use this image. '}
      {'Choose a different one from Docker Hub list'}
    </div>
  )
}

function JobStepRunInDocker({step, stepIndex, onStepPropertyChange}: JobStepRunInDockerProps) {
  const filterRef = useRef<HTMLInputElement>(null)

  const {dockerImageValue: value} = getDockerValues(step)
  const [focused, onFocus, onBlur] = useBooleanState()

  useEffect(() => {
    requestAnimationFrame(() => filterRef.current?.focus())
  }, [])

  const [fetchDockerImages, {images, imagesLoading, imagesError}] =
    restApi.endpoints.getDockerImages.useLazyQuery({
      selectFromResult: ({data, isFetching, error}) => ({
        images: data?.images ?? emptyArray,
        imagesLoading: isFetching,
        imagesError: error,
      }),
    })
  const [fetchDockerTags, {tags, tagsLoading, tagsError}] =
    restApi.endpoints.getDockerTags.useLazyQuery({
      selectFromResult: ({data, isFetching, error}) => ({
        tags: data?.tags ?? emptyArray,
        tagsLoading: isFetching,
        tagsError: error,
      }),
    })

  const {imageName: parsedImageName, tag: parsedTag} = parseDockerImage(value)
  const isTagMode =
    value?.includes(':') && images.find(({name}) => name === parsedImageName) != null
  const isDisabled = useContext(DisabledContext)

  const isLoading = imagesLoading || tagsLoading
  const isError = imagesError != null || tagsError != null
  const isFetchDisabled =
    parsedImageName == null || parsedImageName.length < 2 || !isDockerAutocompleteEnabled

  const options = useMemo(() => {
    if (isFetchDisabled || !isDockerAutocompleteEnabled) {
      return emptyArray
    }
    return isTagMode
      ? getSelectTagsOptions(tags, parsedImageName, value)
      : getSelectImagesOptions(images, value)
  }, [images, isFetchDisabled, isTagMode, parsedImageName, tags, value])

  const hasImage = images.find(({name}) => name === parsedImageName) != null

  const fetchDockerImagesWithThrottle = useMemo(
    () => debounce(fetchDockerImages, THROTTLE_TIME, {leading: true, trailing: true}),
    [fetchDockerImages],
  )
  const fetchDockerTagsWithThrottle = useMemo(
    () => debounce(fetchDockerTags, THROTTLE_TIME, {leading: true, trailing: true}),
    [fetchDockerTags],
  )

  useEffect(() => {
    if (!isFetchDisabled) {
      fetchDockerImagesWithThrottle({q: parsedImageName}) // TODO add the count parameter when it is implemented in the backend

      if (isTagMode && hasImage) {
        fetchDockerTagsWithThrottle({image: parsedImageName, name: parsedTag ?? ''}) // TODO add the count parameter when it is implemented in the backend
      }
    }
  }, [
    fetchDockerImagesWithThrottle,
    fetchDockerTagsWithThrottle,
    isTagMode,
    parsedImageName,
    parsedTag,
    isFetchDisabled,
    hasImage,
  ])

  const onChangeSelect = useCallback(
    (selected: SelectItem | null) => {
      const key = selected?.key
      if (selected == null) {
        onStepPropertyChange(stepIndex, DOCKER_IMAGE_PROPERTY_NAME, '')
      }
      if (typeof key === 'string') {
        onStepPropertyChange(stepIndex, DOCKER_IMAGE_PROPERTY_NAME, key)
      }
      if (typeof key === 'number' && typeof selected?.label === 'string') {
        onStepPropertyChange(stepIndex, DOCKER_IMAGE_PROPERTY_NAME, selected.label)
      }
    },
    [stepIndex, onStepPropertyChange],
  )

  const renderTopToolbar = () => {
    if (!isLoading && value != null && value !== '') {
      if (isError) {
        return <ErrorMessage />
      }
      if (!isFetchDisabled && options.length === 0) {
        return <NotFoundMessage image={value} />
      }
    }

    return null
  }

  return (
    <>
      <Select
        className={styles.select}
        type={Select.Type.INPUT}
        directions={[Directions.BOTTOM_LEFT]}
        filterRef={filterRef}
        inputPlaceholder={'Search Docker Image'}
        filter={{value, fn: () => true}}
        filterIcon={searchIcon}
        disabled={isDisabled}
        data={options}
        notFoundMessage={''}
        label={''}
        renderTopToolbar={renderTopToolbar}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChangeSelect}
        hideArrow
        allowAny
        clear
      />
      <CliErrorMessage
        anchorElement={filterRef.current}
        name={DOCKER_IMAGE_PROPERTY_NAME}
        value={value ?? ''}
        stepIndex={stepIndex}
        focused={focused}
      />
    </>
  )
}

export default memo<JobStepRunInDockerProps>(JobStepRunInDocker)
